import { useHead } from '@solidjs/meta';
import { createUniqueId } from 'solid-js';
import { isServer } from 'solid-js/web';

/**
 * Component that injects JSON-LD structured data into the page head.
 * Uses @solidjs/meta's useHead to ensure proper SSR support.
 *
 * Only renders on server to prevent duplicate scripts during client hydration.
 * Structured data is static and doesn't need client-side updates.
 */
export const StructuredData = (props: { data: Record<string, unknown> }) => {
    // Skip on client - structured data is already in the SSR HTML
    // This prevents @solidjs/meta from adding duplicate scripts during hydration
    if (!isServer) return null;

    const id = createUniqueId();

    useHead({
        tag: 'script',
        props: {
            type: 'application/ld+json',
            children: JSON.stringify(props.data),
        },
        setting: {
            close: true,
            escape: false,
        },
        id: `structured-data-${id}`,
    });

    return null;
};

/**
 * Helper to build the full URL for a page
 */
export const buildFullUrl = (
    protocol: string,
    domain: string | undefined,
    path: string | undefined
): string => {
    const safeDomain = domain || '';
    const safePath = path || '';
    const cleanPath = safePath.startsWith('/') ? safePath : `/${safePath}`;
    return `${protocol}://${safeDomain}${cleanPath}`;
};

/**
 * Helper to get the best available image URL
 */
export const getImageUrl = (
    image: { url?: string; sizes?: { large?: { url?: string } } } | string | undefined
): string | undefined => {
    if (!image) return undefined;
    if (typeof image === 'string') return image;
    return image.sizes?.large?.url || image.url;
};
